import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCalendar,
  FiDollarSign,
  FiUsers,
  FiGrid,
  FiTrendingUp,
  FiArrowRight,
  FiAlertCircle
} from 'react-icons/fi';
import api from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsData, bookingsData] = await Promise.all([
          api.getAdminStats(),
          api.getAdminBookings({ limit: 5, sort: '-createdAt' })
        ]);
        setStats(statsData);
        setRecentBookings(bookingsData.bookings || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      confirmed: { bg: '#E8F5E9', color: '#2E7D32' },
      pending: { bg: '#FFF8E1', color: '#F57C00' },
      'checked-in': { bg: '#E3F2FD', color: '#1976D2' },
      'checked-out': { bg: '#F5F5F5', color: '#616161' },
      cancelled: { bg: '#FFEBEE', color: '#C62828' }
    };
    return colors[status] || colors.pending;
  };

  const styles = {
    header: {
      marginBottom: '32px'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      marginBottom: '8px'
    },
    subtitle: {
      color: '#555'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '20px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    statHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    statIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem'
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '4px'
    },
    statLabel: {
      color: '#555',
      fontSize: '0.875rem'
    },
    statChange: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.75rem',
      marginTop: '8px'
    },
    section: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      marginBottom: '24px'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: '600'
    },
    viewAll: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: '#1a3a5c',
      textDecoration: 'none',
      fontSize: '0.875rem'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '1px solid #eee',
      color: '#555',
      fontWeight: '500',
      fontSize: '0.875rem'
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #f5f5f5'
    },
    badge: {
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600'
    },
    alertCard: {
      background: '#FFF8E1',
      border: '1px solid #FFE082',
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '24px'
    },
    alertIcon: {
      width: '48px',
      height: '48px',
      background: '#FF9800',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem'
    }
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '400px' }}>
        <div className="spinner" />
      </div>
    );
  }

  const pendingBookings = recentBookings.filter(b => b.status === 'pending').length;

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Dashboard</h1>
        <p style={styles.subtitle}>Welcome back! Here's an overview of your property.</p>
      </header>

      {/* Alert for pending bookings */}
      {pendingBookings > 0 && (
        <div style={styles.alertCard}>
          <div style={styles.alertIcon}>
            <FiAlertCircle />
          </div>
          <div>
            <strong>{pendingBookings} pending booking{pendingBookings !== 1 ? 's' : ''}</strong>
            <p style={{ color: '#5D4037', marginTop: '4px' }}>
              Review and confirm new bookings
            </p>
          </div>
          <Link
            to="/admin/bookings?status=pending"
            style={{ marginLeft: 'auto', color: '#E65100', fontWeight: '500' }}
          >
            View All
          </Link>
        </div>
      )}

      {/* Stats Grid */}
      <div style={styles.statsGrid} className="admin-stats-grid">
        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: '#E3F2FD', color: '#1976D2' }}>
              <FiCalendar />
            </div>
          </div>
          <div style={styles.statValue}>{stats?.totalBookings || 0}</div>
          <div style={styles.statLabel}>Total Bookings</div>
          {stats?.bookingsThisMonth && (
            <div style={{ ...styles.statChange, color: '#2E7D32' }}>
              <FiTrendingUp /> {stats.bookingsThisMonth} this month
            </div>
          )}
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: '#E8F5E9', color: '#2E7D32' }}>
              <FiDollarSign />
            </div>
          </div>
          <div style={styles.statValue}>
            £{(stats?.totalRevenue || 0).toLocaleString()}
          </div>
          <div style={styles.statLabel}>Total Revenue</div>
          {stats?.revenueThisMonth && (
            <div style={{ ...styles.statChange, color: '#2E7D32' }}>
              <FiTrendingUp /> £{stats.revenueThisMonth.toLocaleString()} this month
            </div>
          )}
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: '#FFF3E0', color: '#E65100' }}>
              <FiUsers />
            </div>
          </div>
          <div style={styles.statValue}>{stats?.totalUsers || 0}</div>
          <div style={styles.statLabel}>Registered Users</div>
          {stats?.usersThisMonth && (
            <div style={{ ...styles.statChange, color: '#2E7D32' }}>
              <FiTrendingUp /> {stats.usersThisMonth} new this month
            </div>
          )}
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: '#FCE4EC', color: '#C2185B' }}>
              <FiGrid />
            </div>
          </div>
          <div style={styles.statValue}>{stats?.totalApartments || 0}</div>
          <div style={styles.statLabel}>Active Apartments</div>
          <div style={{ ...styles.statChange, color: '#555' }}>
            {stats?.occupancyRate || 0}% occupancy rate
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Recent Bookings</h2>
          <Link to="/admin/bookings" style={styles.viewAll}>
            View all <FiArrowRight />
          </Link>
        </div>

        {recentBookings.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Reference</th>
                <th style={styles.th}>Guest</th>
                <th style={styles.th}>Apartment</th>
                <th style={styles.th}>Dates</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentBookings.map(booking => {
                const statusStyle = getStatusColor(booking.status);
                return (
                  <tr key={booking._id}>
                    <td style={styles.td}>
                      <Link
                        to={`/admin/bookings/${booking._id}`}
                        style={{ color: '#1a3a5c', fontWeight: '500' }}
                      >
                        {booking.bookingReference}
                      </Link>
                    </td>
                    <td style={styles.td}>
                      {booking.guestInfo?.firstName} {booking.guestInfo?.lastName}
                    </td>
                    <td style={styles.td}>{booking.apartment?.name}</td>
                    <td style={styles.td}>
                      {new Date(booking.checkIn).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short'
                      })}
                      {' - '}
                      {new Date(booking.checkOut).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short'
                      })}
                    </td>
                    <td style={styles.td}>
                      £{booking.pricing?.totalPrice?.toFixed(2)}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          background: statusStyle.bg,
                          color: statusStyle.color
                        }}
                      >
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: '#555', padding: '40px' }}>
            No bookings yet
          </p>
        )}
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .admin-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .admin-stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
